/**
 * Product sync page (Phase 65 / BLU-594 PR2 + bulk-selection rework).
 *
 * Deep-linkable tabs (?tab=): Products (expandable per-product SKU
 * derivation preview), Product updates (the platen variant file worklist,
 * shown only to an org with platen size rules) and Code tables (the mapping
 * editor). Mirrors the
 * inventory-app chrome — sticky PageActionBar, brand-purple single accent,
 * bottom-centred FloatingSelectionBar for bulk actions — and shares ONE
 * settings buffer across both tabs (a print-set flip on a product row and a
 * code accept in the mapping screen dirty the same doc). No page-level
 * RoleGuard (mirrors /products); backend permission is truth.
 *
 * SELECTION IS THE RUN SCOPE. The page opens on the work queue
 * (`syncFilter='unsynced'`) and the operator ticks products; "Sync selected"
 * plans exactly the ticked-and-ready set. Selection is CROSS-PAGE, so it is
 * held as a Map of lite records (id/title/type/colours/counts) rather than a
 * Set of ids: completeness (`requiredSets` + `isProductComplete`) has to be
 * computable for a product that is no longer rendered. Design codes are minted
 * ONCE per run, in one pass over the whole assembled selection, so codes can
 * never collide across pages (per-page preview `designCode`s can).
 *
 * Search + status + sync filtering all run SERVER-side (fill-to-page scan) —
 * there is no client-side re-filter of the returned page.
 *
 * STORE SKU MODE (Phase 88, 88a). The preview response's `storeSkuMode` (a
 * composition org with its store SKU switch on) is the only source of the
 * mode: until a page lands the page renders today's controls. In the mode the
 * Status filter offers the store SKU states (`?storeSku=`, a server input for
 * the list and select-all), the Sync filter is hidden, and the action bar's
 * run cluster is ONE "Set up store SKUs" button for admins, which starts the
 * existing job with a declared scope. Every other org sees today's page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { keepPreviousData } from '@tanstack/react-query';
import { AlertTriangle, Link2, Loader2, PackageCheck, PackagePlus, PlayCircle, RefreshCw, RotateCcw, Save, Search, X } from 'lucide-react';
import { byoDesignIdsOn, isCustomSyncProduct, MAX_PRODUCTS_PER_JOB, mintDesignCode } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DisabledReasonTooltip } from '@/components/ui/disabled-reason-tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PageActionBar, PageActionBarDivider } from '@/components/layout/PageActionBar';
import { FloatingSelectionBar } from '@/components/layout/FloatingSelectionBar';
import { BackToTopPill } from '@/components/BackToTopPill';
import { PageErrorBoundary } from '@/components/fallbacks';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import {
  ProductsTab,
  CodeTablesTab,
  RecentSyncRuns,
  BulkMarkAsPapDialog,
  useCoverageScan,
  usePapWorkScan,
  useProductSyncSettingsBuffer,
  useStagedDesigns,
  useArtworkUploadLocation,
  requiredSets,
  isProductComplete,
  isPlatenProductComplete,
  isActiveJobStatus,
} from '@/features/product-sync';
import { CustomizerSection } from '@/features/product-sync/components/customizer/CustomizerSection';
import { CatalogueCopyBanner, type CatalogueRefreshOutcome } from '@/features/product-sync/components/CatalogueCopyBanner';
import type {
  CodeTable,
  FocusMappingRequest,
  ImportFilter,
  PapClashFilter,
  PapStatusFilter,
  PreviewProduct,
  ProductionFilter,
  SelectedProductLite,
  StatusFilter,
  SyncFilter,
} from '@/features/product-sync';
import { distinctColours } from '@/features/product-sync/lib/status';
import type { MappingFilter } from '@/features/product-sync/lib/shared-stock';
import { BulkMapSharedStockDialog } from '@/features/product-sync/components/BulkMapSharedStockDialog';
import { ProductUpdatesTab } from '@/features/product-sync/components/ProductUpdatesTab';
import { useProductUpdatesScan } from '@/features/product-sync/hooks/useProductUpdatesScan';
import {
  PRODUCT_UPDATES_PRODUCT_PARAM,
  parseProductSyncTab,
  type ProductSyncTab,
} from '@/features/product-sync/lib/tabs';
import { PapAddToErpDialog } from '@/features/product-sync/components/PapAddToErpDialog';
import { isPapAddEligible } from '@/features/product-sync/lib/pap-add-eligibility';
import { buildScopeProduct } from '@/features/product-sync/lib/scope';
import { allMatchingSelected, pageSelectAllAction, pageSelectAllState } from '@/features/product-sync/lib/selection';
import {
  STORE_SKU_FILTERS,
  STORE_SKU_FILTER_LABEL_KEYS,
  // `?storeSku=`: the store SKU filter, so a link can land on a filtered Products list.
  STORE_SKU_PARAM,
  buildDeclaredScope,
  parseStoreSkuFilter,
  storeSkuNeedsSetUp,
  storeSkuRunDisabledReason,
  storeSkuRunMode,
  syncCandidates,
  type StoreSkuFilter,
} from '@/features/product-sync/lib/store-skus';
import { PLATEN_READ_STALE_MS, useOrgPlatenContext } from '@/features/product-sync/hooks/usePlatenSizeContext';
import { hasAnyPlatenRule, productUpdatesTabVisible } from '@/features/product-sync/lib/platen';
import {
  scrollProductRowIntoView,
  useMissingFilesRunGate,
  type MissingCheckProduct,
} from '@/features/product-sync/components/MissingFilesDialog';
import { discardStagedDesigns, effectiveByoSets, type PlatenLayout } from '@/features/product-sync/hooks/useStagedDesigns';
import {
  PRODUCT_SYNC_RETURN_KEY,
  PRODUCT_SYNC_SESSION_PAGE,
  StudioReturnContext,
  sameStudioFocus,
  type ProductSyncReturnSnapshot,
  type StudioFocus,
  type StudioReturnApi,
} from '@/features/product-sync/lib/studio-return';
import {
  deletePageSession,
  keepPageSession,
  pageSessionScope,
  readPageSession,
  usePageSessionVisit,
  writePageSession,
} from '@/lib/page-session';
import {
  DeriveSmallContext,
  useDeriveSmallPlatenArtwork,
} from '@/features/product-sync/hooks/useDeriveSmallPlatenArtwork';

const PAGE_SIZE = 25;
/** Back from the studio: the cell stops waiting to be shown after this long (or at the operator's first move). */
const STUDIO_FOCUS_WAIT_MS = 10_000;

/**
 * Runs `effect` when a dep CHANGES, never on mount (StrictMode's second
 * mount run sees the same deps). The page's "a new filter starts over"
 * resets used to run on mount too, which was harmless on a fresh page but
 * would wipe the state Back from the studio restores.
 */
function useOnChange(deps: readonly unknown[], effect: () => void): void {
  const prev = useRef<readonly unknown[] | null>(null);
  const effectRef = useRef(effect);
  effectRef.current = effect;
  useEffect(() => {
    const before = prev.current;
    prev.current = deps;
    if (before && deps.some((d, i) => !Object.is(d, before[i]))) effectRef.current();
    // The deps ARE the trigger; the array is compared by hand above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
/** Bulk Mark-as-PAP is one Shopify write burst per product — kept to a batch an operator can supervise. */
const MAX_PAP_PER_RUN = 50;
const MAX_PAP_ADD_PER_RUN = 200;
/** Mirrors the backend `bulkMapSharedSkus` productIds zod cap (BLU-711 Stage 2). */
const MAX_BULK_MAP_PER_RUN = 100;
/** Backoff after a throttled Mark-as-PAP call before moving to the next product. */
const PAP_THROTTLE_BACKOFF_MS = 2000;

interface PapSummary {
  succeeded: number;
  alreadyTagged: number;
  /** Marked (the tag stands) but not fully added to the ERP: they wait in the PAP view. */
  waiting: { id: string; title: string }[];
  refused: { id: string; title: string }[];
  failed: { id: string; title: string }[];
  /**
   * Phase 88: marked, but the product's store SKU set-up could not be removed
   * (the backend's `warning`). Reported to Sentry server-side; counted here so
   * the summary says so once instead of one toast per product.
   */
  cleanupWarnings: number;
  stopped: boolean;
}

interface PapAddSummary {
  added: number;
  alreadyImported: number;
  partial: number;
  refused: { id: string; title: string }[];
  failed: { id: string; title: string }[];
  stopped: boolean;
}

/** Everything the selection cache needs from a rendered preview row. */
function toLite(p: PreviewProduct): SelectedProductLite {
  return {
    id: p.id,
    title: p.title,
    productTypeName: p.productType.name,
    colours: distinctColours(p),
    variantCount: p.variantCount,
    syncedVariantCount: p.syncedVariantCount,
    shopifyStatus: p.shopifyStatus,
    // Phase 88: only in store SKU mode, so every other record is unchanged.
    ...(p.storeSku ? { storeSkuSettable: p.storeSku.settable } : {}),
    // 86-23: a store SKU BYO org's new product (Sync makes it).
    ...(p.storeSkuNew ? { storeSkuNew: true as const } : {}),
  };
}

/** Structured refusal code forwarded by the backend errorFormatter on `data.cause`. */
function readCauseCode(error: unknown): string | undefined {
  const cause = (error as { data?: { cause?: { code?: string } } } | null | undefined)?.data?.cause;
  return typeof cause?.code === 'string' ? cause.code : undefined;
}

function isThrottled(error: unknown): boolean {
  const data = (error as { data?: { code?: string; httpStatus?: number } } | null | undefined)?.data;
  if (data?.code === 'TOO_MANY_REQUESTS' || data?.httpStatus === 429) return true;
  const message = error instanceof Error ? error.message : '';
  return /\b429\b|throttl|rate limit/i.test(message);
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function StatBadge({
  value,
  label,
  tone = 'default',
  title,
}: { value: number; label: string; tone?: 'default' | 'good' | 'warn'; title?: string }) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-[11px] font-semibold',
        tone === 'warn' ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-border bg-background text-muted-foreground',
      )}
    >
      <span className={cn('text-[12.5px] tabular-nums', tone === 'good' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-800' : 'text-foreground')}>
        {value}
      </span>
      {label}
    </span>
  );
}

/**
 * Filter select trigger that always renders "Label: Value" (never a bare value
 * — "Ready" alone doesn't say which dimension it narrows) and tints
 * brand-purple whenever the value narrows the list.
 *
 * Deliberately NO `aria-label` and no `<SelectValue>`: the visible
 * "Status: Ready" text IS the accessible name, so screen readers get the
 * dimension AND the value. An aria-label would override the content and
 * silence the value.
 */
function FilterTrigger({ label, value, active }: { label: string; value: string; active: boolean }) {
  return (
    <SelectTrigger
      className={cn(
        'h-8 w-[10.5rem] text-xs transition-colors duration-200 motion-reduce:transition-none',
        active && 'border-threadheads-purple/60 text-threadheads-purple',
      )}
    >
      <span className="flex min-w-0 items-center gap-1">
        <span className={cn('shrink-0', active ? 'text-threadheads-purple/70' : 'text-muted-foreground')}>{label}:</span>
        <span className="truncate font-medium">{value}</span>
      </span>
    </SelectTrigger>
  );
}

function ProductSyncPageInner() {
  const { t } = useTranslation('productSync');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, can } = useRole();
  const admin = isAdmin();
  const utils = trpc.useUtils();

  usePageTitle(
    t('page.title'),
    organization ? t('page.subtitle', { org: organization.name, domain: organization.shopify_domain }) : undefined,
  );

  // ---- tab (deep-linked via ?tab=) --------------------------------------
  const [searchParams, setSearchParams] = useSearchParams();
  // Products | Product updates | Code tables. An unknown value falls back to
  // Products (DESIGN.md section 12.1.1); Settings links here as ?tab=updates.
  const activeTab: ProductSyncTab = parseProductSyncTab(searchParams.get('tab'));
  const setActiveTab = useCallback(
    (next: ProductSyncTab) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'products') p.delete('tab');
        else p.set('tab', next);
        return p;
      }, { replace: true });
    },
    [setSearchParams],
  );
  // `?product=` (Product updates only): a print-file issue links straight to
  // one product. The tab scrolls to it once found, then clears the param so a
  // refresh or Back does not replay the jump.
  const focusProductId = activeTab === 'updates' ? searchParams.get(PRODUCT_UPDATES_PRODUCT_PARAM) : null;
  const clearFocusProduct = useCallback(() => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete(PRODUCT_UPDATES_PRODUCT_PARAM);
      return p;
    }, { replace: true });
  }, [setSearchParams]);
  // Phase 88 (S15): the store SKU filter lives on the URL, so Product updates
  // can link straight to the products that need set-up. A server query input
  // like every other filter; the server ignores it outside store SKU mode.
  const storeSkuFilter: StoreSkuFilter = parseStoreSkuFilter(searchParams.get(STORE_SKU_PARAM));
  const setStoreSkuFilter = useCallback(
    (next: StoreSkuFilter) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'all') p.delete(STORE_SKU_PARAM);
        else p.set(STORE_SKU_PARAM, next);
        return p;
      }, { replace: true });
    },
    [setSearchParams],
  );
  // Sent only when it narrows, so an org outside store SKU mode sends exactly
  // today's inputs.
  const storeSkuFilterInput = storeSkuFilter === 'all' ? undefined : storeSkuFilter;

  // ---- Back from the Upload Studio (S6c) ----------------------------------
  // The page session (per org, in memory) holds the staged designs; a studio
  // link also leaves the Products tab as it was (`leaveForStudio` below) and
  // keeps the session for the next visit. Read once here, without consuming
  // it (StrictMode runs this twice); consumed in the effect below. Restored
  // only at the URL it was left from (Back), never on another visit.
  const location = useLocation();
  const here = `${location.pathname}${location.search}`;
  const hereRef = useRef(here);
  hereRef.current = here;
  const sessionScope = useMemo(
    () => pageSessionScope(organization?.id, PRODUCT_SYNC_SESSION_PAGE),
    [organization?.id],
  );
  const [restored] = useState<ProductSyncReturnSnapshot | null>(() => {
    const snapshot = readPageSession<ProductSyncReturnSnapshot>(sessionScope, PRODUCT_SYNC_RETURN_KEY);
    return snapshot && snapshot.from === here ? snapshot : null;
  });
  usePageSessionVisit(sessionScope, discardStagedDesigns);
  useEffect(() => {
    deletePageSession(sessionScope, PRODUCT_SYNC_RETURN_KEY);
  }, [sessionScope]);

  // ---- shared settings buffer + local design staging ---------------------
  const buffer = useProductSyncSettingsBuffer();
  const staging = useStagedDesigns(sessionScope);
  // 86-34 review: ONE Small made from Large queue for the page, beside the
  // staging it reads, so collapsing a product row never drops a queued make
  // or its answer.
  const deriveSmall = useDeriveSmallPlatenArtwork({ isSmallByHand: staging.isSmallByHandNow });
  // Single resolution point for the colour-mode artwork target. OWNED floors
  // first and never a shared 3PL floor by default (D-35); an ambiguous choice
  // resolves to nothing until the operator picks in the colour-mode header.
  const artworkTarget = useArtworkUploadLocation();

  // ---- filters + pagination ---------------------------------------------
  const [searchTerm, setSearchTerm] = useState(() => restored?.searchTerm ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => restored?.searchTerm ?? '');
  // Which catalogue the Products tab shows. POD (default) is the work queue;
  // PAP is the read-only "already handled" catalogue — see ProductionFilter.
  const [productionFilter, setProductionFilter] = useState<ProductionFilter>(() => restored?.productionFilter ?? 'pod');
  const papMode = productionFilter === 'pap';
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => restored?.statusFilter ?? 'all');
  // The page opens on the WORK QUEUE, not the whole catalogue: on a mostly
  // onboarded store "everything" is noise.
  const [syncFilter, setSyncFilter] = useState<SyncFilter>(() => restored?.syncFilter ?? 'unsynced');
  // BLU-711 Stage 2 shared-SKU mapping dimension — inert at 'all'; the server
  // forces it to 'all' anyway while the dimension is dormant or degraded.
  const [mappingFilter, setMappingFilter] = useState<MappingFilter>(() => restored?.mappingFilter ?? 'all');
  // BLU-794. Same reasoning as `syncFilter`'s default, for the PAP catalogue:
  // the tab opens on WORK. Everything the PAP sweep has already imported is
  // noise — Brett, 2026-08-13: "if they are PAP but already synced in the ERP,
  // then they shouldn't really show either."
  const [importFilter, setImportFilter] = useState<ImportFilter>(() => restored?.importFilter ?? 'notImported');
  const [papStatusFilter, setPapStatusFilter] = useState<PapStatusFilter>(() => restored?.papStatusFilter ?? 'all');
  const [papClashFilter, setPapClashFilter] = useState<PapClashFilter>(() => restored?.papClashFilter ?? 'all');
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>(() => restored?.cursorHistory ?? [undefined]);
  const currentCursor = cursorHistory[cursorHistory.length - 1];

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set(restored?.expandedIds ?? []));
  // Cross-page selection: lite records, in STATE (a ref would not re-render the
  // action bar's count or the completeness gate).
  const [selectedProducts, setSelectedProducts] = useState<Map<string, SelectedProductLite>>(
    () => new Map(restored?.selectedProducts ?? []),
  );
  const [selectAllPending, setSelectAllPending] = useState(false);
  const [selectionTruncated, setSelectionTruncated] = useState(() => restored?.selectionTruncated ?? false);
  const [pendingFocus, setPendingFocus] = useState<FocusMappingRequest | null>(null);
  const [overwriteMode, setOverwriteMode] = useState<'skip_existing' | 'overwrite'>(
    () => restored?.overwriteMode ?? 'skip_existing',
  );

  // ---- bulk Map shared stock (BLU-711 Stage 2) ---------------------------
  const [bulkMapDialogOpen, setBulkMapDialogOpen] = useState(false);

  // ---- bulk Mark as PAP --------------------------------------------------
  const [papDialogOpen, setPapDialogOpen] = useState(false);
  const [papProgress, setPapProgress] = useState<{ done: number; total: number } | null>(null);
  const [papSummary, setPapSummary] = useState<PapSummary | null>(null);
  const papStopRef = useRef(false);
  const papRunning = papProgress !== null;

  // ---- PAP queue Add to ERP (single + selected share one runner) ----------
  const [papAddTargets, setPapAddTargets] = useState<Array<Pick<PreviewProduct, 'id' | 'title'> & { shopifyStatus?: PreviewProduct['shopifyStatus'] }>>([]);
  const [papAddDialogOpen, setPapAddDialogOpen] = useState(false);
  const [papAddProgress, setPapAddProgress] = useState<{ done: number; total: number } | null>(null);
  const [papAddSummary, setPapAddSummary] = useState<PapAddSummary | null>(null);
  const [papAddPendingIds, setPapAddPendingIds] = useState<Set<string>>(new Set());
  const papAddStopRef = useRef(false);
  const papAddRunning = papAddProgress !== null;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset pagination + selection when the search changes (a new query is a new
  // universe of matches, so a carried-over selection would be invisible).
  // Each reset runs on a CHANGE only, so a page restored by Back keeps its state.
  useOnChange([debouncedSearch], () => {
    setCursorHistory([undefined]);
    setSelectedProducts(new Map());
    setSelectionTruncated(false);
  });

  // Filters re-page from the start but KEEP the selection: narrowing the view
  // is how an operator assembles a run across several filter passes.
  useOnChange([statusFilter, syncFilter, mappingFilter, importFilter, papStatusFilter, papClashFilter, storeSkuFilter], () => {
    setCursorHistory([undefined]);
    setExpandedIds(new Set());
  });

  // Switching catalogue is NOT a narrowing pass — POD and PAP are different
  // universes with different selection semantics (a PAP row can't join a sync
  // run), so the selection is dropped rather than carried across.
  useOnChange([productionFilter], () => {
    setCursorHistory([undefined]);
    setExpandedIds(new Set());
    setSelectedProducts(new Map());
    setSelectionTruncated(false);
  });

  // ---- Back from the Upload Studio (S6c): leave, then come back ----------
  // The state a studio link leaves behind, read at the click (never stale).
  const leaveStateRef = useRef<Omit<ProductSyncReturnSnapshot, 'focus' | 'from'> | null>(null);
  leaveStateRef.current = {
    searchTerm,
    productionFilter,
    statusFilter,
    syncFilter,
    mappingFilter,
    importFilter,
    papStatusFilter,
    papClashFilter,
    cursorHistory,
    expandedIds: [...expandedIds],
    selectedProducts: [...selectedProducts],
    selectionTruncated,
    overwriteMode,
  };
  const leaveForStudio = useCallback(
    (focus: StudioFocus | null) => {
      if (!sessionScope || !leaveStateRef.current) return;
      writePageSession<ProductSyncReturnSnapshot>(sessionScope, PRODUCT_SYNC_RETURN_KEY, {
        ...leaveStateRef.current,
        from: hereRef.current,
        focus,
      });
      keepPageSession(sessionScope);
    },
    [sessionScope],
  );
  // The cell Back brings into view: its studio link takes it once it is on
  // screen. The operator's first move (a click, a key, the wheel or a touch)
  // or a long wait drops it, so the page never jumps under them.
  const [studioFocus, setStudioFocus] = useState<StudioFocus | null>(() => restored?.focus ?? null);
  const studioFocusRef = useRef(studioFocus);
  studioFocusRef.current = studioFocus;
  const takeStudioFocus = useCallback((focus: StudioFocus): boolean => {
    if (!sameStudioFocus(studioFocusRef.current, focus)) return false;
    studioFocusRef.current = null;
    setStudioFocus(null);
    return true;
  }, []);
  const studioFocusPending = studioFocus !== null;
  useEffect(() => {
    if (!studioFocusPending) return undefined;
    const drop = () => {
      studioFocusRef.current = null;
      setStudioFocus(null);
    };
    const timer = window.setTimeout(drop, STUDIO_FOCUS_WAIT_MS);
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    for (const type of events) window.addEventListener(type, drop, { capture: true, passive: true });
    return () => {
      window.clearTimeout(timer);
      for (const type of events) window.removeEventListener(type, drop, { capture: true });
    };
  }, [studioFocusPending]);

  // "Select all matching" generation guard: bumped whenever the filter/search
  // universe the scan was scoped to moves on, so a slow response that lands
  // after the operator has changed what they're looking at gets dropped
  // instead of resurrecting a selection from a universe they've left.
  const selectAllGenerationRef = useRef(0);
  // The ids the last "Select all matching" fetched, for its Deselect all
  // matching toggle. Scoped to the universe it was fetched in, like the guard.
  const [matchingIds, setMatchingIds] = useState<string[] | null>(null);
  useEffect(() => {
    selectAllGenerationRef.current += 1;
    setMatchingIds(null);
  }, [debouncedSearch, statusFilter, syncFilter, mappingFilter, importFilter, productionFilter, storeSkuFilter]);

  // ---- queries -----------------------------------------------------------
  // POD: one cursor-paged page at a time. PAP: the whole filtered queue,
  // streamed (see `papScan` below) — so this query is DISABLED in PAP mode
  // rather than fetching a page nobody renders.
  const previewQuery = trpc.productSync.getProductSyncPreview.useQuery(
    {
      pageSize: PAGE_SIZE,
      cursor: currentCursor,
      search: debouncedSearch || undefined,
      // In store SKU mode the server forces status and sync to 'all' itself
      // (the same forcing select-all gets), so these stay the page's values:
      // switching them once the mode is known would re-run the same scan.
      statusFilter,
      syncFilter,
      mappingFilter,
      storeSkuFilter: storeSkuFilterInput,
    },
    { placeholderData: keepPreviousData, enabled: !papMode },
  );
  /**
   * BLU-794: the PAP view is a work queue, so it shows the WHOLE filtered set
   * with a counter climbing, not "400 scanned, click Next". Same serial
   * cursor-walk shape as `useCoverageScan` — and still server-filtered, so the
   * browser never receives rows it then hides (CLAUDE.md §10).
   */
  const papScan = usePapWorkScan(papMode, {
    search: debouncedSearch || undefined,
    importFilter,
  });
  // BLU-1528 R3: the catalogue copy's status rides on the POD page and the PAP
  // walk's pages (no read of its own). Get latest shows for the same
  // permission the procedure checks; its answer holds until the state changes.
  const catalogueCopy = papMode ? papScan.catalogueCopy : previewQuery.data?.catalogueCopy;
  const [catalogueRefreshOutcome, setCatalogueRefreshOutcome] = useState<CatalogueRefreshOutcome | null>(null);
  useEffect(() => {
    setCatalogueRefreshOutcome(null);
  }, [catalogueCopy?.state, catalogueCopy?.refreshedAt]);
  const refreshCatalogueCopy = trpc.productSync.refreshCatalogueCopy.useMutation({
    onSuccess: ({ queued }) => {
      setCatalogueRefreshOutcome(queued ? 'queued' : 'not_due');
      void utils.productSync.getProductSyncPreview.invalidate();
    },
    onError: (e) => toast.error(t('catalogueCopy.refreshError', { message: e.message })),
  });
  // BLU-1528 R3: the server could not continue a catalogue copy cursor and
  // sent page 1, so the pager starts over.
  const previewCursorReset = !papMode && previewQuery.data?.cursorReset === true;
  useEffect(() => {
    if (previewCursorReset) setCursorHistory((h) => (h.length > 1 ? [undefined] : h));
  }, [previewCursorReset]);
  // BLU-790: the store-wide scan STREAMS, and only on the tab that consumes it.
  // It used to be a single blocking query mounted on the same tick as
  // `previewQuery` — non-streaming `httpBatchLink` put both in one request, so
  // the product list waited out a 32-45s, 40-page Shopify walk. Every consumer
  // that genuinely needs store-wide data lives on the `code` tab.
  // Keyed by the SAVED tag: the server reads only the saved tag, so typing in
  // the tag input costs nothing and Save starts one fresh walk.
  const coverageScan = useCoverageScan(activeTab === 'code', buffer.serverSettings.syncTag);

  /**
   * Phase 86 (D-14): the Product updates worklist. Walked only while its tab
   * is open (the first open starts it; pages are cached for 5 minutes), and
   * held at page level so the trigger keeps its count across tab switches.
   */
  const productUpdatesScan = useProductUpdatesScan(activeTab === 'updates');

  /**
   * Phase 86: the org's platen context, ONE read for the page. It goes out
   * with the page's first request (batched with the product list, so no extra
   * round trip) and is served from the server's platen context cache. Every
   * expanded row reads it from here to know whether its type is ruled
   * (DesignsTier), and it decides whether Product updates exists at all.
   */
  const orgPlatenContext = useOrgPlatenContext();
  /**
   * Product updates only exists for an org with platen size rules (see
   * `productUpdatesTabVisible`): a green "Nothing needs files" would describe
   * a feature the org does not use.
   */
  const showUpdatesTab = productUpdatesTabVisible({
    requested: activeTab === 'updates',
    worklistRulesConfigured: productUpdatesScan.rulesConfigured,
    orgHasRules: orgPlatenContext.data ? hasAnyPlatenRule(orgPlatenContext.data.rules) : null,
    orgReadFailed: orgPlatenContext.isError,
  });
  // Asked for, and the worklist says there are no rules: back to Products,
  // the same fallback an unknown ?tab= gets, so no tab is ever selected
  // without its trigger. ONE navigation drops both params: the function form
  // of `setSearchParams` is handed this render's params, so two calls in one
  // tick each start from the same URL and the second (which kept `tab`)
  // would win, leaving a hidden tab selected over a blank body.
  useEffect(() => {
    if (activeTab !== 'updates' || productUpdatesScan.rulesConfigured !== false) return;
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('tab');
      p.delete(PRODUCT_UPDATES_PRODUCT_PARAM);
      return p;
    }, { replace: true });
  }, [activeTab, productUpdatesScan.rulesConfigured, setSearchParams]);

  // `keepPreviousData` holds the OUTGOING view's rows on screen while the new
  // one loads. Within a view that is the point (smooth paging); across the
  // POD/PAP flip it would paint POD rows in PAP chrome, where the per-row count
  // means something else entirely. Fall back to the skeleton until the new
  // view's first real page lands.
  const [renderedView, setRenderedView] = useState<ProductionFilter>(productionFilter);
  useEffect(() => {
    // PAP has no placeholder to hold over (it streams from empty), so its first
    // rows landing is what marks the view switched.
    if (papMode ? papScan.products.length > 0 || papScan.isComplete : previewQuery.data !== undefined && !previewQuery.isPlaceholderData) {
      setRenderedView(productionFilter);
    }
  }, [previewQuery.data, previewQuery.isPlaceholderData, productionFilter, papMode, papScan.products.length, papScan.isComplete]);
  const viewSwitching = renderedView !== productionFilter;

  const coverage = coverageScan.coverage;
  // ONE source of rows per view. PAP streams the whole filtered queue; POD
  // pages. Everything below reads these, never a query object directly, so a
  // new source can't be half-wired.
  const previewProducts = useMemo(() => {
    if (!papMode) return previewQuery.data?.products ?? [];
    return papScan.products.filter((product) => {
      if (papStatusFilter !== 'all' && product.shopifyStatus !== papStatusFilter) return false;
      if (papClashFilter === 'all') return true;
      if (papScan.papClashStatusUnavailable) return true;
      const clashes = product.papSkuClash;
      return papClashFilter === 'clashes' ? clashes : !clashes;
    });
  }, [papMode, papScan.products, papScan.papClashStatusUnavailable, previewQuery.data, papStatusFilter, papClashFilter]);
  // Back from the studio, before the cell itself is on screen (its row may
  // still be reading its artwork): bring the product's row into view, so the
  // page never opens at the top. The cell's own link takes over once shown.
  useEffect(() => {
    const focus = studioFocusRef.current;
    if (!focus || !previewProducts.some((p) => p.id === focus.productId)) return;
    scrollProductRowIntoView(focus.productId);
  }, [previewProducts]);
  const studioReturn = useMemo<StudioReturnApi>(
    () => ({
      leaveForStudio,
      locationId: artworkTarget.location?.id ?? null,
      pendingFocus: studioFocus,
      takeFocus: takeStudioFocus,
    }),
    [leaveForStudio, artworkTarget.location?.id, studioFocus, takeStudioFocus],
  );
  const syncStatusUnavailable = previewQuery.data?.syncStatusUnavailable ?? false;
  // BLU-794: the imported-sku lookup failed, so the server treated the import
  // filter as 'all' and every "Imported M/N" chip reads 0.
  const importStatusUnavailable = papMode && papScan.importStatusUnavailable;
  // Dormancy gate (SHARED_LOCATIONS_ENABLED + >=1 active client junction) —
  // false hides the mapping filter and every mapping surface on the rows.
  const sharedSkuUiEnabled = previewQuery.data?.sharedSkuUiEnabled ?? false;
  const mappingStatusUnavailable = previewQuery.data?.mappingStatusUnavailable ?? false;
  // Phase 88: decided by the server, read from the response only. Loading or
  // failed reads as false, so the page shows today's controls until it knows.
  const storeSkuMode = !papMode && previewQuery.data?.storeSkuMode === true;
  // 86-23: a store SKU org in BYO mode. Its new products are derived rows
  // that Sync makes, next to the store SKU rows Set up handles.
  const storeSkuByo = storeSkuMode && previewQuery.data?.storeSkuByo === true;
  // 86-23: `?storeSku=new` only means something in BYO mode. Once the preview
  // has answered for an org outside it, fall back to every product, so a
  // stale link never blanks the list.
  const previewAnswered = previewQuery.data !== undefined;
  useEffect(() => {
    if (storeSkuFilter === 'new' && previewAnswered && !storeSkuByo) setStoreSkuFilter('all');
  }, [storeSkuFilter, previewAnswered, storeSkuByo, setStoreSkuFilter]);
  const scannedProducts = papMode ? papScan.scannedProducts : previewQuery.data?.scan.scannedProducts ?? 0;
  // "This is a window, not the whole set." For PAP that means the walk stopped
  // early (budget dead-end / page cap), not that one page was cut short.
  const scanCapped = papMode ? papScan.stoppedEarly : previewQuery.data?.scan.capped ?? false;
  // PAP streams to the end, so there is nothing to page to.
  const hasNextPage = papMode ? false : previewQuery.data?.hasNextPage ?? false;
  const canPrev = !papMode && cursorHistory.length > 1;

  const seenOptionNames = useMemo(() => {
    const set = new Set<string>();
    // Prefer the store-wide coverage signal when present; fall back to the
    // current preview page's matched options. Optional-safe against the
    // backend field that may not have landed yet ({ colour: [], size: [] }).
    const covSeen = (coverage as { seenOptionNames?: { colour?: string[]; size?: string[] } } | undefined)?.seenOptionNames;
    if (covSeen) {
      for (const n of covSeen.colour ?? []) set.add(n.toLowerCase());
      for (const n of covSeen.size ?? []) set.add(n.toLowerCase());
      return set;
    }
    for (const p of previewProducts) {
      if (p.matchedOptions.colour) set.add(p.matchedOptions.colour.optionName.toLowerCase());
      if (p.matchedOptions.size) set.add(p.matchedOptions.size.optionName.toLowerCase());
    }
    return set;
  }, [coverage, previewProducts]);

  const selectedIds = useMemo(() => new Set(selectedProducts.keys()), [selectedProducts]);
  const selectedCount = selectedProducts.size;
  const selectablePreviewProducts = useMemo(() => papMode
    ? previewProducts.filter((product) => isPapAddEligible(product, {
        importStatusUnavailable: papScan.importStatusUnavailable,
        clashStatusUnavailable: papScan.papClashStatusUnavailable,
      }))
    : previewProducts,
  [papMode, papScan.importStatusUnavailable, papScan.papClashStatusUnavailable, previewProducts]);
  const allOnPageSelected = selectablePreviewProducts.length > 0 && selectablePreviewProducts.every((p) => selectedProducts.has(p.id));
  const everyMatchSelected = allMatchingSelected(matchingIds, (id) => selectedProducts.has(id));

  // Selected products with a complete design assignment (every required set has
  // a ready front source) — the run scope.
  //
  // Derived from SAVED settings (serverSettings), NOT the unsaved buffer: the
  // backend plans/executes from saved settings, so completeness must match what
  // the server will actually resolve (a dirty buffer is separately blocked from
  // running below). Uploads assigned against unsaved dark/light edits would
  // otherwise "complete" a product the server then resolves to a different set.
  //
  // Phase 86 (D-11, D-21): a product whose type has Platen Size rules is read
  // through the `PlatenLayout` snapshot its designs tier wrote (the page only
  // holds variants for its own products, and a selection spans pages). It is
  // complete once each required set has a front in at least one platen
  // variant group. A custom product is never ruled on the server, so it keeps
  // today's path. Custom is the shared rule over the SAVED settings (the type
  // kind, and for a Mixed type this product's own switch), as the server plans.
  const platenLayoutFor = useCallback(
    (p: SelectedProductLite): PlatenLayout | null =>
      isCustomSyncProduct(buffer.serverSettings, p.productTypeName ?? '', p.id) ? null : staging.getPlatenLayout(p.id),
    [buffer.serverSettings, staging],
  );

  // Phase 86 slice B (86-28): an org that uses its own design IDs never mints
  // a code. Each product's design ID and light / dark / medium come from the
  // BYO choice its row wrote into staging on mount (from the preview row the
  // page already holds; open or collapsed alike). A standard product with none
  // is one the reader cannot use (its row dropped the entry), so it is not
  // complete: the plan would refuse it. A custom product never uses BYO: it
  // has no design ID of its own, so it gets a made design ID, as the plan
  // expects (the same per-product rule as the server).
  const byoOn = byoDesignIdsOn(buffer.serverSettings);

  const completeProducts = useMemo(() => {
    if (papMode) return [];
    const out: SelectedProductLite[] = [];
    // 86-23: in store SKU mode only the new products can be synced.
    for (const p of syncCandidates(selectedProducts.values(), storeSkuMode)) {
      // Custom products (the shared rule) are complete with NO designs:
      // artwork arrives per order, not from the catalog.
      const isCustomType = isCustomSyncProduct(buffer.serverSettings, p.productTypeName ?? '', p.id);
      const byoProduct = byoOn && !isCustomType;
      const byo = byoProduct ? staging.getByo(p.id) : null;
      if (byoProduct && !byo) continue;
      const req = requiredSets(
        p.colours,
        buffer.serverSettings.darkColours,
        byo ? Object.values(effectiveByoSets(byo)) : undefined,
      );
      const layout = platenLayoutFor(p);
      const complete = layout
        ? isPlatenProductComplete(staging.get(p.id), layout, req)
        : isProductComplete(staging.get(p.id), req, isCustomType);
      if (complete) out.push(p);
    }
    return out;
  }, [papMode, selectedProducts, buffer.serverSettings, staging, platenLayoutFor, byoOn, storeSkuMode]);
  // 86-23: the selected products a Sync run could take (every one outside store SKU mode).
  const syncCandidateCount = useMemo(
    () => (storeSkuMode ? syncCandidates(selectedProducts.values(), true).length : selectedProducts.size),
    [storeSkuMode, selectedProducts],
  );

  const completeCount = completeProducts.length;
  const incompleteProducts = useMemo(() => {
    const complete = new Set(completeProducts.map((p) => p.id));
    // 86-23: in store SKU mode only the new products can still need designs.
    return syncCandidates([...selectedProducts.values()].filter((p) => !complete.has(p.id)), storeSkuMode);
  }, [completeProducts, selectedProducts, storeSkuMode]);

  // ---- start a sync run --------------------------------------------------
  const startJob = trpc.productSync.startProductSyncJob.useMutation({
    onSuccess: ({ jobId }) => navigate(`/product-sync/jobs/${jobId}`),
    onError: async (e) => {
      if (e.data?.code === 'CONFLICT') {
        try {
          const jobs = await utils.productSync.listProductSyncJobs.fetch();
          const active = jobs.find((j) => isActiveJobStatus(j.status));
          if (active) {
            toast.error(t('run.alreadyRunning'));
            navigate(`/product-sync/jobs/${active.id}`);
            return;
          }
        } catch {
          // fall through to the generic toast
        }
      }
      toast.error(t('run.startError', { message: e.message }));
    },
  });

  // "I'll fix it" on the missing-files dialog (and a name in its notice): open
  // the product and bring its row into view just under the sticky bar. False
  // when the product is on another page, so the gate can say so.
  const focusProductRow = useCallback((id: string): boolean => {
    setExpandedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    return scrollProductRowIntoView(id);
  }, []);
  const expandProductRows = useCallback((ids: string[]) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, []);

  // The S4 flags come from the same cached getPlatenFileState chunks the
  // expanded rows read (D-22): an opened product costs no second request.
  const fetchPlatenFileStateChunk = useCallback(
    (skus: string[]) => utils.productSync.getPlatenFileState.fetch({ skus }, { staleTime: PLATEN_READ_STALE_MS }),
    [utils],
  );
  const {
    run: runMissingFilesGate,
    isChecking: checkingMissingFiles,
    dialog: missingFilesDialog,
    notice: missingFilesNotice,
  } = useMissingFilesRunGate({
    fetchFileStateChunk: fetchPlatenFileStateChunk,
    onFix: focusProductRow,
    onExpand: expandProductRows,
  });

  const handleStartRun = useCallback(() => {
    // Defense-in-depth: never plan from an unsaved buffer (the button is also
    // disabled while dirty). completeProducts is derived from saved settings.
    if (buffer.hasChanges) return;
    if (completeProducts.length === 0 || completeProducts.length > MAX_PRODUCTS_PER_JOB) return;
    if (checkingMissingFiles) return;

    // ONE mint pass over the WHOLE run, against one shared Set: sorted by title
    // so the same selection always mints the same codes, and no two products in
    // the run can land on the same code (per-page preview codes can collide
    // because each request mints against its own empty set).
    const ordered = [...completeProducts].sort((a, b) => a.title.localeCompare(b.title));
    const used = new Set<string>();
    const ruled: MissingCheckProduct[] = [];
    const products = ordered.map((p) => {
      // BYO: the product's own design ID and the operator's choices; no mint.
      // A custom product always gets a made design ID.
      const customProduct = isCustomSyncProduct(buffer.serverSettings, p.productTypeName ?? '', p.id);
      const byo = byoOn && !customProduct ? staging.getByo(p.id) : null;
      const designCode = byo ? byo.designCode : mintDesignCode(p.title, used);
      used.add(designCode);
      // Ruled products send `platenSets` (and `sets: {}`); the rest send
      // today's `sets` byte-for-byte (D-21). A custom product sends no sets,
      // even ones staged before it was switched to custom.
      const layout = platenLayoutFor(p);
      // R4-12: the product's Small by hand platens ride the scope; the
      // missing-files check skips a Small this run will make from Large. S6b:
      // a platen one colour chose by hand counts for the whole platen here
      // (it can only stop a Small being made, never make one).
      const smallByHand = staging.smallByHandPlatensFor(p.id);
      if (layout) {
        const smallMadePlatens = (layout.smallPresetPlatens ?? []).filter((platen) => !smallByHand.includes(platen));
        ruled.push({ productId: p.id, title: p.title, layout, ...(smallMadePlatens.length > 0 ? { smallMadePlatens } : {}) });
      }
      return buildScopeProduct(p.id, designCode, staging.get(p.id), layout, customProduct, byo?.chosen, smallByHand);
    });
    // 86-23: a store SKU org never overwrites (the server refuses it); its
    // Sync only makes new products.
    const overwrite = storeSkuMode ? 'skip_existing' : overwriteMode;
    const start = () => startJob.mutate({ scope: { products, options: { overwrite } } });
    // D-11: warn once about empty platen variant slots before the run starts;
    // with none, the run starts straight away.
    void runMissingFilesGate(ruled, staging.get, start);
  }, [buffer.hasChanges, buffer.serverSettings, completeProducts, staging, overwriteMode, startJob, platenLayoutFor, checkingMissingFiles, runMissingFilesGate, byoOn, storeSkuMode]);

  // ---- store SKU set-up (Phase 88) ----------------------------------------
  // The selected products Set up would act on: at least one variant the
  // declare core would declare (the server's `settable`). Only these go into
  // the declared scope, so the button's count is the job's product count.
  const storeSkuSetUpIds = useMemo(
    () => [...selectedProducts.values()].filter((p) => storeSkuNeedsSetUp(p.storeSkuSettable)).map((p) => p.id),
    [selectedProducts],
  );
  const handleStartStoreSkuSetUp = useCallback(() => {
    // Set up resolves against the SAVED code tables; the button is also
    // disabled while the buffer is dirty.
    if (buffer.hasChanges || storeSkuSetUpIds.length === 0) return;
    // A Sync run's missing-file check is still open; never start both.
    if (checkingMissingFiles) return;
    // A declared plan writes no print rows, so the overwrite mode has no target.
    const result = buildDeclaredScope(storeSkuSetUpIds, { overwrite: 'skip_existing' });
    if (!result.ok) {
      toast.error(t(result.error.key, result.error.values));
      return;
    }
    startJob.mutate({ scope: result.scope });
  }, [buffer.hasChanges, storeSkuSetUpIds, startJob, t, checkingMissingFiles]);

  // ---- selection handlers ------------------------------------------------
  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedProducts((prev) => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      const product = selectablePreviewProducts.find((p) => p.id === id);
      if (!product) return next;
      if (papMode && next.size >= MAX_PAP_ADD_PER_RUN) {
        // Silently doing nothing reads as a broken checkbox. Reuse the existing
        // truncation note rather than inventing a second affordance.
        setSelectionTruncated(true);
        return next;
      }
      next.set(id, toLite(product));
      return next;
    });
  }, [papMode, selectablePreviewProducts]);

  const toggleSelectAll = useCallback(() => {
    setSelectedProducts((prev) => {
      const next = new Map(prev);
      // Measured against what CAN be selected (the PAP cap can stop short of
      // every row). Any row on the page ticked means the click unticks the
      // page, so select all always toggles back off in one click and never
      // re-ticks rows the operator unticked by hand (lib/selection).
      const selectable = selectablePreviewProducts;
      const state = pageSelectAllState(selectable.map((p) => p.id), (id) => next.has(id));
      if (pageSelectAllAction(state) === 'clear') {
        for (const p of selectable) next.delete(p.id);
        return next;
      }
      for (const p of selectable) {
        if (!papMode || next.size < MAX_PAP_ADD_PER_RUN) next.set(p.id, toLite(p));
      }
      if (papMode && selectable.some((p) => !next.has(p.id))) setSelectionTruncated(true);
      return next;
    });
  }, [papMode, selectablePreviewProducts]);

  const clearSelection = useCallback(() => {
    // Invalidate any in-flight "select all matching" fetch — its response
    // landing after a Clear must not resurrect the cleared selection.
    selectAllGenerationRef.current += 1;
    setSelectedProducts(new Map());
    setSelectionTruncated(false);
    setMatchingIds(null);
  }, []);

  // "Deselect all matching": unticks exactly what Select all matching ticked,
  // keeping picks from earlier filter passes.
  const deselectAllMatching = useCallback(() => {
    if (!matchingIds) return;
    selectAllGenerationRef.current += 1;
    setSelectedProducts((prev) => {
      const next = new Map(prev);
      for (const id of matchingIds) next.delete(id);
      return next;
    });
    setSelectionTruncated(false);
    setMatchingIds(null);
  }, [matchingIds]);

  const handleSelectAllMatching = useCallback(async () => {
    setSelectAllPending(true);
    const requestGeneration = selectAllGenerationRef.current;
    try {
      const result = await utils.productSync.getProductSyncMatchingIds.fetch({
        search: debouncedSearch || undefined,
        statusFilter,
        syncFilter,
        mappingFilter,
        storeSkuFilter: storeSkuFilterInput,
      });
      // The filter/search universe moved on while the scan was in flight —
      // applying this response now would resurrect a selection scoped to a
      // universe the operator is no longer looking at.
      if (selectAllGenerationRef.current !== requestGeneration) return;
      // Fail CLOSED: if the sync-status lookup failed, the scan can't tell
      // synced from unsynced, so it treated the filter as 'all'. Selecting
      // nothing is safe; selecting everything is not (silently overrides the
      // operator's synced/unsynced filter).
      // Store SKU mode ignores the sync filter server-side, so a sync lookup
      // failure cannot widen the selection there.
      if (result.syncStatusUnavailable && !storeSkuMode) {
        toast.error(t('bulkBar.selectAllUnavailable'));
        return;
      }
      // Same fail-closed posture for the mapping dimension: when its lookup
      // degraded the scan treated the filter as 'all' — selecting everything
      // would silently override the operator's mapping filter.
      if (mappingFilter !== 'all' && result.mappingStatusUnavailable) {
        toast.error(t('mappingFilter.selectAllUnavailable'));
        return;
      }
      setSelectedProducts((prev) => {
        const next = new Map(prev);
        for (const p of result.products) {
          next.set(p.id, {
            id: p.id,
            title: p.title,
            productTypeName: p.productTypeName,
            colours: p.colours,
            variantCount: p.variantCount,
            syncedVariantCount: p.syncedVariantCount,
            ...(p.storeSkuSettable !== undefined ? { storeSkuSettable: p.storeSkuSettable } : {}),
            ...(p.storeSkuNew ? { storeSkuNew: true as const } : {}),
          });
        }
        return next;
      });
      setMatchingIds(result.products.map((p) => p.id));
      // PERSISTENT note, not a toast: a partial selection stays true for as
      // long as the selection exists.
      setSelectionTruncated(result.truncated);
    } catch (error) {
      toast.error(t('bulkBar.selectAllError', { message: error instanceof Error ? error.message : String(error) }));
    } finally {
      setSelectAllPending(false);
    }
  }, [utils, debouncedSearch, statusFilter, syncFilter, mappingFilter, storeSkuFilterInput, storeSkuMode, t]);

  // ---- bulk Mark as PAP --------------------------------------------------
  const markAsPap = trpc.productSync.markProductAsPap.useMutation();

  // A half-finished classification sweep must not be lost to a stray reload.
  useEffect(() => {
    if (!papRunning && !papAddRunning) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [papRunning, papAddRunning]);

  const runBulkMarkAsPap = useCallback(async () => {
    const targets = [...selectedProducts.values()].slice(0, MAX_PAP_PER_RUN);
    if (targets.length === 0) return;

    papStopRef.current = false;
    setPapSummary(null);
    setPapProgress({ done: 0, total: targets.length });

    let succeeded = 0;
    let alreadyTagged = 0;
    const waiting: { id: string; title: string }[] = [];
    const refused: { id: string; title: string }[] = [];
    const failed: { id: string; title: string }[] = [];
    const settledIds: string[] = [];
    let cleanupWarnings = 0;
    let stopped = false;

    for (let i = 0; i < targets.length; i++) {
      if (papStopRef.current) {
        stopped = true;
        break;
      }
      const target = targets[i]!;
      let backOff = false;
      try {
        const result = await markAsPap.mutateAsync({ productId: target.id });
        // The tag stands either way; a product the ERP import did not fully
        // take is named so the operator knows to finish it in the PAP view.
        if (result.erpImport.status === 'waiting' || result.erpImport.status === 'partially_imported') {
          waiting.push({ id: target.id, title: target.title });
        } else if (result.alreadyTagged) alreadyTagged += 1;
        else succeeded += 1;
        if (result.warning) cleanupWarnings += 1;
        settledIds.push(target.id);
      } catch (error) {
        // POD_APPLIED is a deliberate refusal (the product is already onboarded
        // as POD), not a failure — bucket it off the structured cause, never a
        // message match.
        if (readCauseCode(error) === 'POD_APPLIED') {
          refused.push({ id: target.id, title: target.title });
        } else {
          failed.push({ id: target.id, title: target.title });
          backOff = isThrottled(error);
        }
      }
      // Progress first, THEN the throttle backoff: a frozen counter during a
      // 2s sleep reads as a hang.
      setPapProgress({ done: i + 1, total: targets.length });
      if (backOff) await sleep(PAP_THROTTLE_BACKOFF_MS);
    }

    // Settled products have left the POD candidate list — drop them from the
    // selection so the remaining count is the remaining work.
    if (settledIds.length > 0) {
      setSelectedProducts((prev) => {
        const next = new Map(prev);
        for (const id of settledIds) next.delete(id);
        return next;
      });
    }
    setPapProgress(null);
    setPapSummary({ succeeded, alreadyTagged, waiting, refused, failed, cleanupWarnings, stopped });
    void utils.productSync.getProductSyncPreview.invalidate();
    void utils.productSync.getProductSyncCoverage.invalidate();
  }, [selectedProducts, markAsPap, utils]);

  const updatePapSkusMutation = trpc.productSync.updatePapVariantSkus.useMutation();
  const addPapProductMutation = trpc.productSync.addPapProductToErp.useMutation();

  const loadPapResolution = useCallback(
    (productId: string) => utils.productSync.getPapSkuResolution.fetch({ productId }),
    [utils],
  );

  // Depend on the stable functions, not the containers: `papScan` and the
  // mutation object are fresh on every render, and this callback is handed to every row.
  const applyPapWorkStates = papScan.applyWorkStateUpdates;
  const updatePapSkusMutateAsync = updatePapSkusMutation.mutateAsync;
  const updatePapSkus = useCallback(async (input: Parameters<typeof updatePapSkusMutation.mutateAsync>[0]) => {
    const result = await updatePapSkusMutateAsync(input);
    applyPapWorkStates(result.affectedWorkStates);
    return result;
  }, [applyPapWorkStates, updatePapSkusMutateAsync]);

  const requestPapAdd = useCallback((product: PreviewProduct) => {
    setPapAddTargets([product]);
    setPapAddDialogOpen(true);
  }, []);

  const requestSelectedPapAdd = useCallback(() => {
    if (papScan.importStatusUnavailable || papScan.papClashStatusUnavailable) return;
    const selected = [...selectedProducts.values()]
      .slice(0, MAX_PAP_ADD_PER_RUN);
    if (selected.length === 0) return;
    setPapAddTargets(selected);
    setPapAddDialogOpen(true);
  }, [papScan.importStatusUnavailable, papScan.papClashStatusUnavailable, selectedProducts]);

  const runPapAdd = useCallback(async () => {
    const targets = papAddTargets.slice(0, MAX_PAP_ADD_PER_RUN);
    if (targets.length === 0) return;
    papAddStopRef.current = false;
    setPapAddSummary(null);
    setPapAddProgress({ done: 0, total: targets.length });
    let added = 0;
    let alreadyImported = 0;
    let partial = 0;
    let stopped = false;
    const refused: PapAddSummary['refused'] = [];
    const failed: PapAddSummary['failed'] = [];
    const settledIds: string[] = [];

    for (let index = 0; index < targets.length; index += 1) {
      if (papAddStopRef.current) { stopped = true; break; }
      const target = targets[index]!;
      setPapAddPendingIds((current) => new Set(current).add(target.id));
      let backOff = false;
      try {
        const result = await addPapProductMutation.mutateAsync({ productId: target.id });
        papScan.applyWorkStateUpdates([result.workState]);
        if (result.status === 'added') added += 1;
        else if (result.status === 'partially_imported') partial += 1;
        else alreadyImported += 1;
        settledIds.push(target.id);
        if (targets.length === 1) toast.success(result.draftInventoryHeld ? t('papActions.addedDraft') : t('papActions.added'));
      } catch (error) {
        const code = readCauseCode(error);
        if (['sku_conflict', 'stale_sku', 'invalid_product', 'no_importable_skus', 'variants_truncated'].includes(code ?? '')) {
          refused.push({ id: target.id, title: target.title });
        } else {
          failed.push({ id: target.id, title: target.title });
          // Mirror runBulkMarkAsPap: a Shopify 429 is retryable, and firing the
          // next product immediately turns one throttle into a cascade across
          // the whole run — every item of which then reports as a permanent
          // failure.
          backOff = isThrottled(error);
        }
        if (targets.length === 1) toast.error(error instanceof Error ? error.message : t('papActions.addFailed'));
      } finally {
        setPapAddPendingIds((current) => {
          const next = new Set(current);
          next.delete(target.id);
          return next;
        });
        setPapAddProgress({ done: index + 1, total: targets.length });
      }
      // Progress first, THEN the backoff: a frozen counter during the sleep
      // reads as a hang.
      if (backOff) await sleep(PAP_THROTTLE_BACKOFF_MS);
    }
    if (settledIds.length > 0) {
      setSelectedProducts((current) => {
        const next = new Map(current);
        for (const id of settledIds) next.delete(id);
        return next;
      });
    }
    setPapAddProgress(null);
    setPapAddSummary({ added, alreadyImported, partial, refused, failed, stopped });
    void utils.productSync.getProductSyncPreview.invalidate();
  }, [addPapProductMutation, papAddTargets, papScan, t, utils]);

  // ---- handlers ----------------------------------------------------------
  const handleMapIt = useCallback((table: CodeTable, name?: string) => {
    setPendingFocus({ table, name });
    setActiveTab('code');
  }, [setActiveTab]);

  const handleRefresh = useCallback(() => {
    // PAP's walk caches each page for 5 minutes, so invalidating alone would
    // re-walk stale-but-fresh pages and hand back the identical list —
    // `rescan()` bumps the generation as well.
    if (papMode) {
      papScan.rescan();
      return;
    }
    void utils.productSync.getProductSyncPreview.invalidate();
    void utils.productSync.getProductSyncCoverage.invalidate();
  }, [utils, papMode, papScan]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearch('');
    // Each view clears only the filters it actually renders — resetting the
    // other catalogue's filters behind the operator's back would silently
    // change a view they aren't looking at.
    if (papMode) {
      setImportFilter('all');
      setPapStatusFilter('all');
      setPapClashFilter('all');
      return;
    }
    setStatusFilter('all');
    setSyncFilter('all');
    setMappingFilter('all');
    setStoreSkuFilter('all');
  }, [papMode, setStoreSkuFilter]);

  const goNext = useCallback(() => {
    const endCursor = previewQuery.data?.endCursor;
    if (previewQuery.data?.hasNextPage && endCursor) {
      setCursorHistory((h) => [...h, endCursor]);
      setExpandedIds(new Set());
    }
  }, [previewQuery.data]);

  const goPrev = useCallback(() => {
    setCursorHistory((h) => (h.length > 1 ? h.slice(0, -1) : h));
    setExpandedIds(new Set());
  }, []);

  // ---- derived counts ----------------------------------------------------
  /**
   * BLU-790: filter against the LIVE settings buffer, exactly as `MappingCard`
   * does (`unmappedAll.filter((v) => !hasName(codeMap, v.name))`).
   *
   * The scan's raw "values seen unmapped" is computed server-side against saved
   * settings, so accepting a code would leave this badge stale until a rescan.
   * Re-deriving here means a save updates the count instantly AND costs no
   * Shopify calls — which is why saving a code table deliberately does not
   * invalidate the streamed pages.
   */
  const valuesNeedCodes = useMemo(() => {
    if (!coverage) return 0;
    const stillUnmapped = (values: { name: string }[], codeMap: Record<string, string>) => {
      const mapped = new Set(Object.keys(codeMap).map((k) => k.trim().toLowerCase()));
      return values.filter((v) => !mapped.has(v.name.trim().toLowerCase())).length;
    };
    return (
      stillUnmapped(coverage.unmappedValues.productTypes, buffer.settings.productTypes) +
      stillUnmapped(coverage.unmappedValues.colours, buffer.settings.colours) +
      stillUnmapped(coverage.unmappedValues.sizes, buffer.settings.sizes)
    );
  }, [coverage, buffer.settings]);

  // BLU-790: the code tab no longer waits on the scan. `buffer` is a single
  // fast DB read and carries the mapping cards, the sync-tag input and the
  // anatomy hero; only three regions consume coverage, and they stream. ORing
  // the scan back in here would re-create the head-of-line block at the render
  // layer — the exact thing this change removes at the network layer.
  const codeTablesLoading = buffer.isLoading;
  const codeTablesError = buffer.isError;

  const statusFilterLabels: Record<StatusFilter, string> = {
    all: t('filter.all'),
    ready: t('filter.ready'),
    attention: t('filter.needsAttention'),
  };
  const syncFilterLabels: Record<SyncFilter, string> = {
    all: t('syncFilter.all'),
    synced: t('syncFilter.synced'),
    unsynced: t('syncFilter.unsynced'),
  };
  // Bulk shared-stock mapping is only honest from the SYNCED view: the
  // backend skips (and reports) every unsynced product, so offering the
  // action elsewhere invites a run that skips most of the selection. Also
  // paused while the sync-status lookup is down (everything would report
  // syncStatusUnknown).
  const bulkMapEnabled = syncFilter === 'synced' && !syncStatusUnavailable && !papMode;

  const importFilterLabels: Record<ImportFilter, string> = {
    all: t('importFilter.all'),
    notImported: t('importFilter.notImported'),
    imported: t('importFilter.imported'),
  };
  const papStatusFilterLabels: Record<PapStatusFilter, string> = {
    all: t('papStatusFilter.all'),
    draft: t('papStatusFilter.draft'),
    active: t('papStatusFilter.active'),
  };
  const papClashFilterLabels: Record<PapClashFilter, string> = {
    all: t('papClashFilter.all'),
    clashes: t('papClashFilter.clashes'),
    clear: t('papClashFilter.clear'),
  };

  const mappingFilterLabels: Record<MappingFilter, string> = {
    all: t('mappingFilter.all'),
    mapped: t('mappingFilter.mapped'),
    pending: t('mappingFilter.pending'),
    unmapped: t('mappingFilter.unmapped'),
    attention: t('mappingFilter.attention'),
  };

  // ---- action-bar clusters ----------------------------------------------
  const syncSelect = (
    <Select value={syncFilter} onValueChange={(v) => setSyncFilter(v as SyncFilter)} disabled={syncStatusUnavailable}>
      <FilterTrigger
        label={t('syncFilter.label')}
        value={syncFilterLabels[syncFilter]}
        active={syncFilter !== 'all'}
      />
      <SelectContent>
        <SelectItem value="all">{t('syncFilter.all')}</SelectItem>
        <SelectItem value="unsynced">{t('syncFilter.unsynced')}</SelectItem>
        <SelectItem value="synced">{t('syncFilter.synced')}</SelectItem>
      </SelectContent>
    </Select>
  );

  // BLU-794 — the PAP view's counterpart of `syncSelect`, same shape and same
  // degradation posture (disabled + tooltip when the lookup is down).
  const importSelect = (
    <Select
      value={importFilter}
      onValueChange={(v) => setImportFilter(v as ImportFilter)}
      disabled={importStatusUnavailable}
    >
      <FilterTrigger
        label={t('importFilter.label')}
        value={importFilterLabels[importFilter]}
        active={importFilter !== 'all'}
      />
      <SelectContent>
        <SelectItem value="all">{t('importFilter.all')}</SelectItem>
        <SelectItem value="notImported">{t('importFilter.notImported')}</SelectItem>
        <SelectItem value="imported">{t('importFilter.imported')}</SelectItem>
      </SelectContent>
    </Select>
  );
  const papStatusSelect = (
    <Select value={papStatusFilter} onValueChange={(value) => setPapStatusFilter(value as PapStatusFilter)}>
      <FilterTrigger label={t('papStatusFilter.label')} value={papStatusFilterLabels[papStatusFilter]} active={papStatusFilter !== 'all'} />
      <SelectContent>
        <SelectItem value="all">{t('papStatusFilter.all')}</SelectItem>
        <SelectItem value="draft">{t('papStatusFilter.draft')}</SelectItem>
        <SelectItem value="active">{t('papStatusFilter.active')}</SelectItem>
      </SelectContent>
    </Select>
  );
  const papClashSelect = (
    <Select
      value={papClashFilter}
      onValueChange={(value) => setPapClashFilter(value as PapClashFilter)}
      disabled={papScan.papClashStatusUnavailable}
    >
      <FilterTrigger label={t('papClashFilter.label')} value={papClashFilterLabels[papClashFilter]} active={papClashFilter !== 'all'} />
      <SelectContent>
        <SelectItem value="all">{t('papClashFilter.all')}</SelectItem>
        <SelectItem value="clashes">{t('papClashFilter.clashes')}</SelectItem>
        <SelectItem value="clear">{t('papClashFilter.clear')}</SelectItem>
      </SelectContent>
    </Select>
  );

  const mappingSelect = (
    <Select
      value={mappingFilter}
      onValueChange={(v) => setMappingFilter(v as MappingFilter)}
      disabled={mappingStatusUnavailable}
    >
      <FilterTrigger
        label={t('mappingFilter.label')}
        value={mappingFilterLabels[mappingFilter]}
        active={mappingFilter !== 'all'}
      />
      <SelectContent>
        <SelectItem value="all">{t('mappingFilter.all')}</SelectItem>
        <SelectItem value="mapped">{t('mappingFilter.mapped')}</SelectItem>
        <SelectItem value="pending">{t('mappingFilter.pending')}</SelectItem>
        <SelectItem value="unmapped">{t('mappingFilter.unmapped')}</SelectItem>
        <SelectItem value="attention">{t('mappingFilter.attention')}</SelectItem>
      </SelectContent>
    </Select>
  );

  const productsLeft = (
    <>
      {/* Which catalogue: the POD work queue, or the PAP products already handed
          to the sweep. Segmented control mirrors the tab list beside it and
          /products' own POD/PAP switch (no TabsContent — same segmented-control
          idiom as CurrencyTogglePill, DESIGN §7). */}
      <Tabs value={productionFilter} onValueChange={(v) => setProductionFilter(v as ProductionFilter)}>
        <TabsList className="h-8" aria-label={t('papView.switchAria')}>
          <TabsTrigger value="pod" disabled={papRunning || papAddRunning} className="h-6 px-3 text-xs" aria-label={t('papView.podAria')}>
            {t('papView.pod')}
          </TabsTrigger>
          <TabsTrigger value="pap" disabled={papRunning || papAddRunning} className="h-6 px-3 text-xs" aria-label={t('papView.papAria')}>
            {t('papView.pap')}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="relative w-56 md:w-64">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
          placeholder={papMode ? t('papView.searchPlaceholder') : t('search.placeholder')}
          aria-label={papMode ? t('papView.searchLabel') : t('search.label')}
          className="h-8 pl-9 text-xs"
        />
      </div>
      {/* BLU-794 — PAP's ONE narrowing filter, in the slot the POD status/sync
          selects occupy. Same degradation treatment as the sync filter: a
          filter over a signal the scan couldn't load would be a lie. */}
      {papMode && (importStatusUnavailable ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex">{importSelect}</span>
          </TooltipTrigger>
          <TooltipContent>{t('importFilter.unavailable')}</TooltipContent>
        </Tooltip>
      ) : (
        importSelect
      ))}
      {papMode && papStatusSelect}
      {papMode && (!papScan.papClashStatusUnavailable ? papClashSelect : (
        <Tooltip>
          <TooltipTrigger asChild><span tabIndex={0} className="inline-flex">{papClashSelect}</span></TooltipTrigger>
          <TooltipContent>{t('papClashFilter.unavailable')}</TooltipContent>
        </Tooltip>
      ))}
      {/* Status + sync narrow a DERIVATION queue; the server forces both to
          'all' in PAP mode, so rendering them there would be a lie. */}
      {/* Phase 88: store SKU mode swaps the derivation states for the store
          SKU ones, in the same slot and the same trigger. */}
      {storeSkuMode && (
        <Select value={storeSkuFilter} onValueChange={(v) => setStoreSkuFilter(parseStoreSkuFilter(v))}>
          <FilterTrigger
            label={t('filter.label')}
            value={t(STORE_SKU_FILTER_LABEL_KEYS[storeSkuFilter])}
            active={storeSkuFilter !== 'all'}
          />
          <SelectContent>
            {/* 86-23: "New products" only where there can be any (BYO mode). */}
            {STORE_SKU_FILTERS.filter((value) => storeSkuByo || value !== 'new').map((value) => (
              <SelectItem key={value} value={value}>{t(STORE_SKU_FILTER_LABEL_KEYS[value])}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {!papMode && !storeSkuMode && (
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <FilterTrigger
            label={t('filter.label')}
            value={statusFilterLabels[statusFilter]}
            active={statusFilter !== 'all'}
          />
          <SelectContent>
            <SelectItem value="all">{t('filter.all')}</SelectItem>
            <SelectItem value="ready">{t('filter.ready')}</SelectItem>
            <SelectItem value="attention">{t('filter.needsAttention')}</SelectItem>
          </SelectContent>
        </Select>
      )}
      {/* The server ignores the sync filter in store SKU mode, so it is not
          offered there. */}
      {!papMode && !storeSkuMode && (
        <DisabledReasonTooltip reason={syncStatusUnavailable ? t('syncFilter.unavailable') : null}>{syncSelect}</DisabledReasonTooltip>
      )}
      {/* Mapping filter renders ONLY while the shared-SKU dimension is live
          (dormancy gate) — a filter over signals the scan never loaded would
          be a lie. Degrades like the sync filter when the lookup failed. */}
      {!papMode && sharedSkuUiEnabled && (
        <DisabledReasonTooltip reason={mappingStatusUnavailable ? t('mappingFilter.unavailable') : null}>{mappingSelect}</DisabledReasonTooltip>
      )}
      {/*
        BLU-790: the three store-wide badges (products / ready / needs attention)
        used to sit here. They were store-wide and filter-blind next to a
        filter-scoped list — the mixing CLAUDE.md §10 forbids — AND truncated at
        2,000 of 3,555, so for Threadheads they were simply wrong. Their honest
        successor is the single "needs attention" badge on the code tab, where
        the scan that produces it now lives. Row-level status plus the
        "Needs attention" filter carry orientation here.
      */}
    </>
  );

  // A dirty buffer blocks the run: the server plans from SAVED settings, so a
  // run started with unsaved code-table edits would resolve variants against a
  // different dark/light split than the operator staged files for.
  const runDisabledReason = papProgress
    ? t('bulkPap.progress', { done: papProgress.done, total: papProgress.total })
    : selectedCount === 0
      ? t('bulkBar.needSelection')
      : syncCandidateCount === 0
        ? t('bulkBar.needNewProduct')
      : completeCount === 0
        ? t('bulkBar.needDesignsReason')
        : buffer.hasChanges
          ? t('actions.previewRunDirty')
          : completeCount > MAX_PRODUCTS_PER_JOB
            ? t('bulkBar.overCap', { max: MAX_PRODUCTS_PER_JOB })
            : null;
  const canStartRun = admin && runDisabledReason === null && !startJob.isPending && !checkingMissingFiles;
  // 86-23: Set up and Sync share one mutation; each spins only for its own run.
  const pendingScope = startJob.isPending ? startJob.variables?.scope : undefined;
  const setUpPending = pendingScope !== undefined && 'identity' in pendingScope && pendingScope.identity === 'declared';
  const syncPending = checkingMissingFiles || (startJob.isPending && !setUpPending);
  const syncButton = (
    <Button
      size="sm"
      disabled={!canStartRun}
      onClick={handleStartRun}
      className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
    >
      {syncPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <PlayCircle className="h-3.5 w-3.5" aria-hidden="true" />}
      {completeCount > 0 ? t('bulkBar.syncSelectedCount', { count: completeCount }) : t('bulkBar.syncSelected')}
    </Button>
  );

  // Phase 88: which run cluster renders (F1). `store_skus` is ONE Set up
  // button in place of the overwrite select and Sync; `none` is a non-admin in
  // store SKU mode (statuses only); `sync` is today's cluster.
  const runMode = storeSkuRunMode({ storeSkuMode, admin, storeSkuByo });
  const setUpShown = runMode === 'store_skus' || runMode === 'store_skus_and_sync';
  const setUpCount = storeSkuSetUpIds.length;
  const storeSkuDisabled = storeSkuRunDisabledReason({
    selected: selectedCount,
    needSetUp: setUpCount,
    bufferDirty: buffer.hasChanges,
    max: MAX_PRODUCTS_PER_JOB,
    allNew: selectedCount > 0 && syncCandidateCount === selectedCount,
  });
  const setUpDisabledReason = papProgress
    ? t('bulkPap.progress', { done: papProgress.done, total: papProgress.total })
    : storeSkuDisabled
      ? t(storeSkuDisabled.key, storeSkuDisabled.values)
      : null;
  const canStartSetUp = setUpShown && setUpDisabledReason === null && !startJob.isPending && !checkingMissingFiles;
  const setUpButton = (
    <Button
      size="sm"
      disabled={!canStartSetUp}
      onClick={handleStartStoreSkuSetUp}
      className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
    >
      {setUpPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <PlayCircle className="h-3.5 w-3.5" aria-hidden="true" />}
      {setUpCount > 0 ? t('bar.setUpStoreSkus', { count: setUpCount }) : t('bar.setUpStoreSkusIdle')}
    </Button>
  );

  // 86-23: the same run as Sync selected, for the new products only.
  const storeSkuSyncButton = (
    <Button
      variant="outline"
      size="sm"
      disabled={!canStartRun}
      onClick={handleStartRun}
      className="h-8 gap-1.5 px-3 text-xs"
    >
      {syncPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <PlayCircle className="h-3.5 w-3.5" aria-hidden="true" />}
      {completeCount > 0 ? t('bulkBar.syncNewCount', { count: completeCount }) : t('bulkBar.syncNew')}
    </Button>
  );

  const productsRight = (
    <>
      <Button variant="outline" size="sm" onClick={handleRefresh} className="h-8 gap-1.5 px-3 text-xs">
        {/* Coverage no longer runs on this tab, so its fetching state can never
            be true here — the list is the only thing this button refreshes. */}
        {(papMode ? papScan.isScanning : previewQuery.isFetching) ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {t('actions.refresh')}
      </Button>
      {/* Overwrite mode + "Sync selected" are POD-run machinery: a PAP product
          has no derived print rows to write, so neither has a target here. */}
      {admin && !papMode && runMode === 'sync' && (
        <>
          <PageActionBarDivider />
          <Select value={overwriteMode} onValueChange={(v) => setOverwriteMode(v as 'skip_existing' | 'overwrite')}>
            <SelectTrigger className="h-8 w-40 text-xs" aria-label={t('run.overwriteLabel')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="skip_existing">{t('run.skipExisting')}</SelectItem>
              <SelectItem value="overwrite">{t('run.overwrite')}</SelectItem>
            </SelectContent>
          </Select>
          <DisabledReasonTooltip reason={runDisabledReason}>{syncButton}</DisabledReasonTooltip>
        </>
      )}
      {setUpShown && (
        <>
          <PageActionBarDivider />
          {/* 86-23: Sync for the new products, next to Set up; the pair wraps together. */}
          {runMode === 'store_skus_and_sync' && (
            <DisabledReasonTooltip reason={runDisabledReason} hint={t('bulkBar.storeSkuSyncHint')}>
              {storeSkuSyncButton}
            </DisabledReasonTooltip>
          )}
          <DisabledReasonTooltip reason={setUpDisabledReason}>{setUpButton}</DisabledReasonTooltip>
        </>
      )}
    </>
  );

  const codeLeft = (
    <>
      <StatBadge value={3} label={t('stat.tablesLabel', { count: 3 })} />
      {valuesNeedCodes > 0 && (
        <StatBadge value={valuesNeedCodes} label={t('stat.valuesNeedCodesLabel', { count: valuesNeedCodes })} tone="warn" />
      )}
      {/*
        BLU-790: the ONE store-wide badge worth keeping, relocated from the
        products bar to the tab whose scan produces it. `unmappedProducts` is
        this tab's outcome metric — a non-ready product means unmapped values or
        an unrecognised option, both fixed here. `ready` was its arithmetic
        complement (the same number said twice) and `products` now appears as
        the subject of the scan-progress line, so neither came along.
      */}
      {coverage && coverage.totals.unmappedProducts > 0 && (
        <StatBadge
          value={coverage.totals.unmappedProducts}
          label={t('stat.productsBlockedLabel', { count: coverage.totals.unmappedProducts })}
          tone="warn"
        />
      )}
    </>
  );

  const codeRight = admin ? (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={buffer.discard}
        disabled={!buffer.hasChanges || buffer.isSaving}
        className="h-8 gap-1.5 px-3 text-xs"
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
        {t('actions.discard')}
      </Button>
      <PageActionBarDivider />
      <Button
        size="sm"
        onClick={buffer.save}
        disabled={!buffer.hasChanges || buffer.isSaving}
        className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
      >
        {buffer.isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Save className="h-3.5 w-3.5" aria-hidden="true" />}
        {t('actions.save')}
      </Button>
    </>
  ) : null;

  // The updates tab's own left cluster adds nothing; its right keeps Refresh only.
  const updatesRight = (
    <Button
      variant="outline"
      size="sm"
      onClick={productUpdatesScan.rescan}
      disabled={productUpdatesScan.isScanning}
      className="h-8 gap-1.5 px-3 text-xs"
    >
      {productUpdatesScan.isScanning ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
      ) : (
        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {t('actions.refresh')}
    </Button>
  );

  const page = cursorHistory.length;

  const papSummaryLine = papSummary
    ? [
        t('bulkPap.summaryMarked', { count: papSummary.succeeded }),
        t('bulkPap.summaryAlready', { count: papSummary.alreadyTagged }),
        t('bulkPap.summaryWaiting', { count: papSummary.waiting.length }),
        t('bulkPap.summaryRefused', { count: papSummary.refused.length }),
        t('bulkPap.summaryFailed', { count: papSummary.failed.length }),
      ].join(' · ')
    : '';
  const papAddSummaryLine = papAddSummary
    ? [
        t('papBulk.summaryAdded', { count: papAddSummary.added }),
        t('papBulk.summaryPartial', { count: papAddSummary.partial }),
        t('papBulk.summaryAlready', { count: papAddSummary.alreadyImported }),
        t('papBulk.summaryRefused', { count: papAddSummary.refused.length }),
        t('papBulk.summaryFailed', { count: papAddSummary.failed.length }),
      ].join(' · ')
    : '';

  return (
    <div className="flex flex-col">
      <div className="container max-w-full px-6 py-6">
        <StudioReturnContext.Provider value={studioReturn}>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(parseProductSyncTab(v))}>
          <PageActionBar
            ariaLabel={t('page.title')}
            left={
              <>
                <TabsList className="h-8">
                  <TabsTrigger value="products" disabled={papRunning || papAddRunning} className="h-6 px-3 text-xs">{t('tabs.products')}</TabsTrigger>
                  {showUpdatesTab && (
                    <TabsTrigger value="updates" disabled={papRunning || papAddRunning} className="h-6 gap-1.5 px-3 text-xs">
                      {t('tabs.productUpdates')}
                      {/* Every segment carries its count once a walk has folded a page. */}
                      {productUpdatesScan.count > 0 && (
                        <span className="text-[11px] tabular-nums text-muted-foreground">{productUpdatesScan.count}</span>
                      )}
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="code" disabled={papRunning || papAddRunning} className="h-6 px-3 text-xs">{t('tabs.codeTables')}</TabsTrigger>
                </TabsList>
                {activeTab !== 'updates' && <PageActionBarDivider />}
                {activeTab === 'products' ? productsLeft : activeTab === 'code' ? codeLeft : null}
              </>
            }
            right={activeTab === 'products' ? productsRight : activeTab === 'code' ? codeRight : updatesRight}
          />

          <TabsContent value="products" className="mt-6">
            {papMode && papAddSummary && (
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-lg border bg-muted/40 px-4 py-3" role="status">
                <div className="min-w-0 space-y-1">
                  <p className="text-xs font-semibold">
                    {papAddSummary.stopped ? t('papBulk.summaryStoppedTitle') : t('papBulk.summaryTitle')}
                  </p>
                  <p className="text-xs tabular-nums text-muted-foreground">{papAddSummaryLine}</p>
                  {papAddSummary.refused.length > 0 && (
                    <p className="text-xs text-amber-800">
                      <span className="font-semibold">{t('papBulk.refusedLabel')}: </span>
                      {papAddSummary.refused.map((product) => product.title).join(', ')}
                    </p>
                  )}
                  {papAddSummary.failed.length > 0 && (
                    <p className="text-xs text-destructive">
                      <span className="font-semibold">{t('papBulk.failedLabel')}: </span>
                      {papAddSummary.failed.map((product) => product.title).join(', ')}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPapAddSummary(null)}
                  aria-label={t('papBulk.dismissAria')}
                  className="h-8 gap-1.5 px-3 text-xs"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('papBulk.dismiss')}
                </Button>
              </div>
            )}
            {/* Bulk Mark-as-PAP outcome — persistent + dismissable, never a
                toast: it names products that still need a decision. */}
            {papSummary && (
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-lg border bg-muted/40 px-4 py-3" role="status">
                <div className="min-w-0 space-y-1">
                  <p className="text-xs font-semibold">
                    {papSummary.stopped ? t('bulkPap.summaryStoppedTitle') : t('bulkPap.summaryTitle')}
                  </p>
                  <p className="text-xs tabular-nums text-muted-foreground">{papSummaryLine}</p>
                  {papSummary.cleanupWarnings > 0 && (
                    <p className="text-xs text-amber-800">
                      {t('bulkPap.summaryCleanupWarning', { count: papSummary.cleanupWarnings })}
                    </p>
                  )}
                  {papSummary.waiting.length > 0 && (
                    <p className="text-xs text-amber-800">
                      <span className="font-semibold">{t('bulkPap.waitingLabel')}: </span>
                      {papSummary.waiting.map((p) => p.title).join(', ')}
                    </p>
                  )}
                  {papSummary.refused.length > 0 && (
                    <p className="text-xs text-amber-800">
                      <span className="font-semibold">{t('bulkPap.refusedLabel')}: </span>
                      {papSummary.refused.map((p) => p.title).join(', ')}
                    </p>
                  )}
                  {papSummary.failed.length > 0 && (
                    <p className="text-xs text-destructive">
                      <span className="font-semibold">{t('bulkPap.failedLabel')}: </span>
                      {papSummary.failed.map((p) => p.title).join(', ')}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPapSummary(null)}
                  aria-label={t('bulkPap.dismissAria')}
                  className="h-8 gap-1.5 px-3 text-xs"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('bulkPap.dismiss')}
                </Button>
              </div>
            )}
            {/* Visible truncated-scan caveat (not tooltip-only) — mirrors the
                Code tables note. Caveats the POD store totals, so it leaves with
                them in PAP mode. */}
            {/* BLU-790: the store-scan truncation note lived here. The scan no
                longer runs on this tab, and its honest successor — a progress
                line, not a cap — sits on the code tab beside the lists it
                qualifies. The window caveat below is the products tab's own. */}
            {catalogueCopy && !viewSwitching && (
              <CatalogueCopyBanner
                status={catalogueCopy}
                canRefresh={can('org_settings.read')}
                onRefresh={() => refreshCatalogueCopy.mutate()}
                refreshPending={refreshCatalogueCopy.isPending}
                refreshOutcome={catalogueRefreshOutcome}
              />
            )}
            {/* The fill-scan stopped early: this page is a WINDOW, not the
                whole filtered set. Suppressed mid-switch, where the numbers
                still describe the outgoing view. */}
            {scanCapped && !viewSwitching && (
              <p className="mb-3 flex items-center gap-1.5 text-xs text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t('scan.capped', { count: scannedProducts })}
              </p>
            )}
            {/* Only meaningful while the sync filter is on screen: in PAP mode
                the imported count comes from the sweep, not the projection. */}
            {!papMode && !storeSkuMode && syncStatusUnavailable && (
              <p className="mb-3 text-xs text-amber-700">{t('syncFilter.unavailable')}</p>
            )}
            {/* Its PAP counterpart — the import filter silently became 'all',
                so the list is the whole PAP catalogue, not the work queue. */}
            {papMode && importStatusUnavailable && (
              <p className="mb-3 text-xs text-amber-700">{t('importFilter.unavailable')}</p>
            )}
            {/* Honest degradation for the mapping dimension — visible note,
                never tooltip-only (mirrors the sync-status note above). */}
            {!papMode && sharedSkuUiEnabled && mappingStatusUnavailable && (
              <p className="mb-3 text-xs text-amber-700">{t('mappingFilter.unavailable')}</p>
            )}
            {/* Inline dirty save bar — also the visible "save before running"
                hint (the run plans from saved settings, not the buffer). */}
            {admin && buffer.hasChanges && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5">
                <span className="text-xs font-medium text-amber-900">{t('actions.previewRunDirty')}</span>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={buffer.discard} disabled={buffer.isSaving} className="h-8 gap-1.5 px-3 text-xs text-amber-800 hover:bg-amber-100">
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('actions.discard')}
                  </Button>
                  <Button size="sm" onClick={buffer.save} disabled={buffer.isSaving} className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90">
                    {buffer.isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Save className="h-3.5 w-3.5" aria-hidden="true" />}
                    {t('actions.save')}
                  </Button>
                </div>
              </div>
            )}
            {admin && !papMode && <div className="mb-3 empty:hidden">{missingFilesNotice}</div>}
            {/* 86-34 review: the page's one Small made from Large queue outlives a collapsed row. */}
            <DeriveSmallContext.Provider value={deriveSmall}>
              <ProductsTab
                products={previewProducts}
                hasCustomPrintSkuMapping={previewQuery.data?.hasCustomPrintSkuMapping ?? true}
                // A carried-over page from the other catalogue is not this view's
                // data — show the skeleton until the new view's page lands. Never
                // over an error though: the placeholder page never resolves, so
                // `viewSwitching` would pin the skeleton forever and swallow the
                // error block (ProductsTab checks loading before error).
                //
                // PAP renders PROGRESSIVELY: the skeleton is only for the gap
                // before the first page lands. After that the rows are real and
                // the counter carries the "still working" signal — replacing a
                // filling list with a skeleton on every page would be worse than
                // the pager it replaced.
                isLoading={
                  papMode
                    ? papScan.error === null && papScan.products.length === 0 && !papScan.isComplete && !papScan.stoppedEarly
                    : !previewQuery.isError && (previewQuery.isLoading || viewSwitching)
                }
                isError={papMode ? papScan.error !== null : previewQuery.isError}
                errorMessage={papMode ? papScan.error : previewQuery.error?.message ?? null}
                onRetry={papMode ? papScan.rescan : () => void previewQuery.refetch()}
                // PAP only: the walk is still running, so "0 rows" is not yet an
                // answer and no empty state may claim otherwise.
                isStreaming={papMode && !papScan.isComplete && papScan.error === null && !papScan.stoppedEarly}
                streamComplete={papMode && papScan.isComplete}
                query={debouncedSearch}
                searchActive={debouncedSearch.length > 0}
                // Both predicate filters are forced to 'all' server-side in PAP
                // mode, so they are never "active" there.
                // Store SKU mode: its own filter is the status filter, and the
                // sync filter is ignored server-side.
                statusFilterActive={storeSkuMode ? storeSkuFilter !== 'all' : !papMode && statusFilter !== 'all'}
                syncFilterActive={!papMode && !storeSkuMode && syncFilter !== 'all'}
                // Mirrors the sync filter: when the mapping lookup degraded the
                // server treated the filter as 'all', so it isn't narrowing.
                mappingFilterActive={!papMode && sharedSkuUiEnabled && !mappingStatusUnavailable && mappingFilter !== 'all'}
                sharedSkuUiEnabled={sharedSkuUiEnabled}
                mappingStatusUnavailable={mappingStatusUnavailable}
                // When the projection lookup failed the server ignored the sync
                // filter, so an empty page must NOT claim "everything is synced".
                syncFilterIsUnsynced={!papMode && !storeSkuMode && syncFilter === 'unsynced' && !syncStatusUnavailable}
                // Same contract on the PAP side: a degraded lookup means the
                // server ignored the filter, so an empty page must NOT claim
                // "everything is imported".
                importFilterActive={papMode && importFilter !== 'all' && !importStatusUnavailable}
                importFilterIsNotImported={papMode && importFilter === 'notImported' && !importStatusUnavailable}
                scannedProducts={scannedProducts}
                syncStatusUnavailable={syncStatusUnavailable}
                importStatusUnavailable={importStatusUnavailable}
                scanCapped={scanCapped}
                onClearFilters={clearFilters}
                papMode={papMode}
                papExtraFiltersActive={papMode && (papStatusFilter !== 'all' || papClashFilter !== 'all')}
                papClaimsComplete={!papScan.papClashStatusUnavailable}
                papClaimsError={papScan.papClashStatusUnavailable ? t('papClashFilter.unavailable') : null}
                papPendingIds={papAddPendingIds}
                onRequestPapAdd={requestPapAdd}
                onLoadPapResolution={loadPapResolution}
                onUpdatePapSkus={updatePapSkus}
                expandedIds={expandedIds}
                onToggleExpand={toggleExpand}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                page={page}
                totalCount={papMode ? undefined : previewQuery.data?.totalCount}
                canPrev={canPrev}
                canNext={hasNextPage}
                onPrev={goPrev}
                onNext={goNext}
                isFetching={papMode ? papScan.isScanning : previewQuery.isFetching}
                buffer={buffer}
                staging={staging}
                onMapIt={handleMapIt}
                admin={admin}
                artworkTarget={artworkTarget}
              />
            </DeriveSmallContext.Provider>
            <RecentSyncRuns />
          </TabsContent>

          {showUpdatesTab && (
            <TabsContent value="updates" className="mt-6">
              <ProductUpdatesTab
                scan={productUpdatesScan}
                artworkTarget={artworkTarget}
                admin={admin}
                focusProductId={focusProductId}
                onFocusHandled={clearFocusProduct}
              />
            </TabsContent>
          )}

          <TabsContent value="code" className="mt-6">
            <CodeTablesTab
              coverage={coverage}
              bufferLoading={codeTablesLoading}
              coverageLoading={coverage === null && coverageScan.error === null}
              scannedProducts={coverageScan.scannedProducts}
              coverageComplete={coverageScan.isComplete}
              coverageScanning={coverageScan.isScanning}
              coverageError={coverageScan.error}
              onRescan={coverageScan.rescan}
              isError={codeTablesError}
              errorMessage={buffer.errorMessage}
              onRetry={() => {
                coverageScan.rescan();
                buffer.refetch();
              }}
              buffer={buffer}
              admin={admin}
              pendingFocus={pendingFocus}
              onFocusConsumed={() => setPendingFocus(null)}
              seenOptionNames={seenOptionNames}
            />
            <CustomizerSection />
          </TabsContent>
        </Tabs>
        </StudioReturnContext.Provider>
      </div>

      {/* Selection bar lives at PAGE level, outside TabsContent: a parent that
          unmounts kills the shared primitive's exit animation. */}
      <FloatingSelectionBar
        visible={selectedCount > 0 && activeTab === 'products'}
        ariaLabel={t('bulkBar.toolbarAria')}
        srStatus={
          papAddProgress
            ? t('papBulk.progress', { done: papAddProgress.done, total: papAddProgress.total })
            : papProgress
            ? t('bulkPap.progress', { done: papProgress.done, total: papProgress.total })
            : papMode
              ? t('papBulk.srStatus', { count: selectedCount })
              : storeSkuMode
                ? t('bulkBar.srStatusStoreSkuJoin', {
                    selected: t('bulkBar.srStatusStoreSku', { count: selectedCount }),
                    needed: t('bulkBar.srStatusStoreSkuNeeded', { count: setUpCount }),
                  })
                : t('bulkBar.srStatus', { count: selectedCount, ready: completeCount })
        }
        accessory={
          (papAddRunning || papRunning || selectionTruncated) && (
            <div className="flex flex-col items-center gap-2">
              {(papAddProgress || papProgress) && (
                <div className="flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-xs shadow-md">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-threadheads-purple" aria-hidden="true" />
                  <span className="tabular-nums">
                    {papMode && papAddProgress
                      ? t('papBulk.progress', { done: papAddProgress.done, total: papAddProgress.total })
                      : papProgress && t('bulkPap.progress', { done: papProgress.done, total: papProgress.total })}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 rounded-full px-2 text-xs"
                    onClick={() => {
                      if (papMode) papAddStopRef.current = true;
                      else papStopRef.current = true;
                    }}
                    aria-label={papMode ? t('papBulk.stopAria') : t('bulkPap.stopAria')}
                  >
                    {papMode ? t('papBulk.stop') : t('bulkPap.stop')}
                  </Button>
                </div>
              )}
              {selectionTruncated && (
                <div className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-800 shadow-sm">
                  {t('bulkBar.selectAllTruncated', { count: MAX_PRODUCTS_PER_JOB })}
                </div>
              )}
            </div>
          )
        }
      >
        {papMode ? (
          <>
            <span className="whitespace-nowrap text-sm font-medium tabular-nums">
              {t('selection.count', { count: selectedCount })}
            </span>
            <div className="mx-1.5 h-5 w-px bg-border" aria-hidden="true" />
            <Button
              size="sm"
              className="gap-1.5 rounded-full bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              onClick={requestSelectedPapAdd}
              disabled={papAddRunning || papScan.importStatusUnavailable || papScan.papClashStatusUnavailable}
            >
              <PackagePlus className="h-3.5 w-3.5" aria-hidden="true" />
              {t('papBulk.addSelected', { count: selectedCount })}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 rounded-full"
              onClick={clearSelection}
              aria-label={t('selection.clear')}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              {t('bulkBar.clear')}
            </Button>
          </>
        ) : (
          <>
            <span className="whitespace-nowrap text-sm font-medium tabular-nums">
              {t('selection.count', { count: selectedCount })}
              {/* "Ready" and "need designs" describe a sync run; store SKU
                  mode has one only in BYO mode, for its new products (86-23). */}
              {(!storeSkuMode || storeSkuByo) && (
                <span className="font-normal text-muted-foreground"> · {t('bulkBar.readyCount', { count: completeCount })}</span>
              )}
            </span>
        {(!storeSkuMode || storeSkuByo) && incompleteProducts.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('bulkBar.needDesignsAria', { count: incompleteProducts.length })}
                className="gap-1.5 rounded-full text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              >
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                {t('bulkBar.needDesignsCount', { count: incompleteProducts.length })}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="center" className="w-72 p-0">
              <div className="border-b px-3 py-2">
                <p className="text-xs font-semibold">{t('bulkBar.needDesignsTitle')}</p>
                <p className="text-[11px] text-muted-foreground">{t('bulkBar.needDesignsHint')}</p>
              </div>
              <ul className="max-h-52 divide-y overflow-y-auto">
                {incompleteProducts.map((p) => (
                  <li key={p.id} className="truncate px-3 py-1.5 text-xs">{p.title}</li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        )}
        {/* One button, two words: once every match is ticked it unticks them
            again, so this select all toggles back off too (BLU-1522 S6a). */}
        {everyMatchSelected ? (
          <Button variant="ghost" size="sm" className="gap-1.5 rounded-full" onClick={deselectAllMatching}>
            {t('bulkBar.deselectAllMatching')}
          </Button>
        ) : allOnPageSelected && (hasNextPage || canPrev) ? (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 rounded-full"
            onClick={() => void handleSelectAllMatching()}
            disabled={selectAllPending}
          >
            {selectAllPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            {t('bulkBar.selectAllMatching')}
          </Button>
        ) : null}
        <div className="mx-1.5 h-5 w-px bg-border" aria-hidden="true" />
        {admin && sharedSkuUiEnabled && (
          // Bulk mapping is SYNCED-scoped server-side (unsynced products are
          // skipped and reported) — only offer it live from the Synced view so
          // "N products mapped" can never silently mean "N products skipped".
          bulkMapEnabled ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 rounded-full"
              onClick={() => setBulkMapDialogOpen(true)}
            >
              <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
              {t('bulkMap.action')}
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex">
                  <Button variant="outline" size="sm" className="gap-1.5 rounded-full" disabled>
                    <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('bulkMap.action')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {syncStatusUnavailable ? t('bulkMap.disabledSyncUnknown') : t('bulkMap.disabledNeedSynced')}
              </TooltipContent>
            </Tooltip>
          )
        )}
        {admin && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 rounded-full"
            onClick={() => setPapDialogOpen(true)}
            disabled={papRunning}
          >
            <PackageCheck className="h-3.5 w-3.5" aria-hidden="true" />
            {t('bulkPap.action')}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 rounded-full"
          onClick={clearSelection}
          aria-label={t('selection.clear')}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
          {t('bulkBar.clear')}
        </Button>
          </>
        )}
      </FloatingSelectionBar>

      {admin && sharedSkuUiEnabled && (
        <BulkMapSharedStockDialog
          open={bulkMapDialogOpen}
          onOpenChange={setBulkMapDialogOpen}
          products={[...selectedProducts.values()]}
          maxPerRun={MAX_BULK_MAP_PER_RUN}
        />
      )}

      {admin && (
        <BulkMarkAsPapDialog
          open={papDialogOpen}
          onOpenChange={setPapDialogOpen}
          products={[...selectedProducts.values()]}
          maxPerRun={MAX_PAP_PER_RUN}
          onConfirm={() => {
            setPapDialogOpen(false);
            void runBulkMarkAsPap();
          }}
        />
      )}

      {admin && (
        <PapAddToErpDialog
          open={papAddDialogOpen}
          onOpenChange={setPapAddDialogOpen}
          products={papAddTargets}
          onConfirm={() => {
            setPapAddDialogOpen(false);
            void runPapAdd();
          }}
        />
      )}

      {admin && missingFilesDialog}

      <BackToTopPill />
    </div>
  );
}

export default function ProductSyncPage() {
  return (
    <PageErrorBoundary>
      <ProductSyncPageInner />
    </PageErrorBoundary>
  );
}
